/**
 * Debounce utility - delays function execution until after a specified delay
 * Useful for search inputs, filters, and other rapid user interactions
 * 
 * @param {Function} func - The function to debounce
 * @param {number} delay - Delay in milliseconds
 * @returns {Function} Debounced function
 * 
 * @example
 * const debouncedSearch = debounce((value) => {
 *   console.log('Searching for:', value);
 * }, 300);
 * 
 * // In component:
 * <input onChange={(e) => debouncedSearch(e.target.value)} />
 */
export function debounce(func, delay = 300) {
  let timeoutId;
  
  return function debounced(...args) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      func.apply(this, args);
    }, delay);
  };
}

/**
 * Throttle utility - limits function execution to at most once per specified interval
 * Useful for button clicks, scroll events, and other actions that should be rate-limited
 * 
 * @param {Function} func - The function to throttle
 * @param {number} limit - Time limit in milliseconds
 * @returns {Function} Throttled function
 * 
 * @example
 * const throttledClick = throttle(() => {
 *   console.log('Button clicked');
 * }, 1000);
 * 
 * // In component:
 * <button onClick={throttledClick}>Click me</button>
 */
export function throttle(func, limit = 1000) {
  let inThrottle;
  
  return function throttled(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => {
        inThrottle = false;
      }, limit);
    }
  };
}

// Note: useDebounce hook should be in a separate file if needed
// For now, we'll use debounce and throttle functions directly in components

